/**
 * API service for Decadent Palete frontend
 * Replaces mock data with real backend API calls
 */

import { getBackendURL } from '../utils/config';

const API_BASE_URL = getBackendURL();

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Helper method for making API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}/api${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  // Newsletter API
  async subscribeToNewsletter(email) {
    return this.request('/newsletter/subscribe', {
      method: 'POST',
      body: { email },
    });
  }

  async unsubscribeFromNewsletter(email) {
    return this.request(`/newsletter/unsubscribe/${email}`, {
      method: 'DELETE',
    });
  }

  // Pop-up Events API
  async signupForEvents(email) {
    return this.request('/events/signup', {
      method: 'POST',
      body: { email },
    });
  }

  async unsubscribeFromEvents(email) {
    return this.request(`/events/unsubscribe/${email}`, {
      method: 'DELETE',
    });
  }

  // Inquiries API
  async submitEventInquiry(inquiryData) {
    return this.request('/inquiries/event', {
      method: 'POST',
      body: inquiryData,
    });
  }

  async submitCakeInquiry(inquiryData) {
    return this.request('/inquiries/cake', {
      method: 'POST',
      body: inquiryData,
    });
  }

  // Subscriptions API
  async subscribeToTier(subscriptionData) {
    return this.request('/subscriptions/subscribe', {
      method: 'POST',
      body: subscriptionData,
    });
  }

  async getUserSubscription(email) {
    return this.request(`/subscriptions/user/${email}`);
  }

  // Partnership API
  async submitPartnershipApplication(applicationData) {
    return this.request('/partnerships/apply', {
      method: 'POST',
      body: applicationData,
    });
  }

  async getPartnershipTypes() {
    return this.request('/partnerships/types');
  }

  // Gift Cards API
  async purchaseGiftCard(giftCardData) {
    return this.request('/gift-cards/purchase', {
      method: 'POST',
      body: giftCardData,
    });
  }

  async verifyGiftCard(cardCode) {
    return this.request(`/gift-cards/verify/${cardCode}`);
  }

  async redeemGiftCard(cardCode, amount) {
    return this.request(`/gift-cards/redeem/${cardCode}?redeem_amount=${amount}`, {
      method: 'POST',
    });
  }

  async getUserGiftCards(email) {
    return this.request(`/gift-cards/user/${email}`);
  }

  // Referrals API
  async createReferral(referralData) {
    return this.request('/referrals/create', {
      method: 'POST',
      body: referralData,
    });
  }

  async convertReferral(referralCode, subscriptionAmount) {
    return this.request(`/referrals/convert/${referralCode}?subscription_amount=${subscriptionAmount}`, {
      method: 'POST',
    });
  }

  async getUserReferrals(email) {
    return this.request(`/referrals/user/${email}`);
  }

  async getCommissionRates() {
    return this.request('/referrals/commission-rates');
  }

  // Analytics API
  async trackPageView(page, userAgent = null, ipAddress = null) {
    const params = new URLSearchParams({ page });
    if (userAgent) params.append('user_agent', userAgent);
    if (ipAddress) params.append('ip_address', ipAddress);

    return this.request(`/analytics/track-page-view?${params}`, {
      method: 'POST',
    });
  }

  // Health check
  async healthCheck() {
    return this.request('/health');
  }

  // Blog API
  async getBlogPosts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/blog/posts${queryString ? `?${queryString}` : ''}`);
  }

  async getBlogPost(slug) {
    return this.request(`/blog/posts/${slug}`);
  }

  async getFeaturedPosts(limit = 3) {
    return this.request(`/blog/featured?limit=${limit}`);
  }

  async getRecentPosts(limit = 5) {
    return this.request(`/blog/recent?limit=${limit}`);
  }

  async getBlogCategories() {
    return this.request('/blog/categories');
  }

  async getBlogTags(limit = 20) {
    return this.request(`/blog/tags?limit=${limit}`);
  }

  async getPostComments(postId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/blog/posts/${postId}/comments${queryString ? `?${queryString}` : ''}`);
  }

  async createComment(postId, commentData) {
    return this.request(`/blog/posts/${postId}/comments`, {
      method: 'POST',
      body: commentData,
    });
  }

  // Admin blog endpoints
  async createBlogPost(postData) {
    return this.request('/blog/admin/posts', {
      method: 'POST',
      body: postData,
    });
  }

  async updateBlogPost(postId, postData) {
    return this.request(`/blog/admin/posts/${postId}`, {
      method: 'PUT',
      body: postData,
    });
  }

  async deleteBlogPost(postId) {
    return this.request(`/blog/admin/posts/${postId}`, {
      method: 'DELETE',
    });
  }

  // Feedback API
  async submitFeedback(feedbackData) {
    return this.request('/feedback/submit', {
      method: 'POST',
      body: feedbackData,
    });
  }

  async getFeedbackStats() {
    return this.request('/feedback/stats');
  }

  async getPublicFeedback(limit = 10) {
    return this.request(`/feedback/public?limit=${limit}`);
  }

  // Charity/Volunteer API
  async submitVolunteerApplication(applicationData) {
    return this.request('/charity/volunteer/apply', {
      method: 'POST',
      body: applicationData,
    });
  }

  async getCharityImpact() {
    return this.request('/charity/impact');
  }

  async getCharityPartners() {
    return this.request('/charity/partners');
  }

  async getCharityEvents() {
    return this.request('/charity/events');
  }

  // Farm-to-Table Wine Experience API
  async submitFarmToTableBooking(bookingData) {
    return this.request('/farm-to-table/booking', {
      method: 'POST',
      body: bookingData,
    });
  }

  async getFarmToTableStats() {
    return this.request('/farm-to-table/stats');
  }

  async getWinePairings(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/farm-to-table/wine-pairings${queryString ? '?' + queryString : ''}`);
  }

  async getFarmPartners(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/farm-to-table/farm-partners${queryString ? '?' + queryString : ''}`);
  }

  async getWineEvents(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/farm-to-table/wine-events${queryString ? '?' + queryString : ''}`);
  }

  // Gift Packages API
  async submitGiftPackageOrder(orderData) {
    return this.request('/gift-packages/order', {
      method: 'POST',
      body: orderData,
    });
  }

  async getGiftPackages(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/gift-packages${queryString ? '?' + queryString : ''}`);
  }

  async getGiftPackageStats() {
    return this.request('/gift-packages/stats');
  }

  async getGiftPackageOrders(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/gift-packages/orders${queryString ? '?' + queryString : ''}`);
  }

  // Corporate Membership API
  async submitCorporateInquiry(inquiryData) {
    return this.request('/corporate/inquiry', {
      method: 'POST',
      body: inquiryData,
    });
  }

  // Testimonial Management APIs
  async getPublicTestimonials(params = {}) {
    const queryString = new URLSearchParams();
    
    if (params.is_featured !== undefined) {
      queryString.append('is_featured', params.is_featured);
    }
    if (params.limit) {
      queryString.append('limit', params.limit);
    }
    if (params.offset) {
      queryString.append('offset', params.offset);
    }

    return this.request(`/testimonials/public${queryString.toString() ? '?' + queryString.toString() : ''}`);
  }

  async submitTestimonial(testimonialData) {
    return this.request('/testimonials/', {
      method: 'POST',
      body: testimonialData,
    });
  }

  // Admin Testimonial APIs (for future admin panel)
  async getAllTestimonials(params = {}) {
    const queryString = new URLSearchParams();
    
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryString.append(key, params[key]);
      }
    });

    return this.request(`/testimonials${queryString.toString() ? '?' + queryString.toString() : ''}`);
  }

  async updateTestimonial(testimonialId, updateData) {
    return this.request(`/testimonials/${testimonialId}`, {
      method: 'PATCH',
      body: updateData,
    });
  }

  async approveTestimonial(testimonialId) {
    return this.request(`/testimonials/${testimonialId}/approve`, {
      method: 'POST',
    });
  }

  async featureTestimonial(testimonialId) {
    return this.request(`/testimonials/${testimonialId}/feature`, {
      method: 'POST',
    });
  }

  async deleteTestimonial(testimonialId) {
    return this.request(`/testimonials/${testimonialId}`, {
      method: 'DELETE',
    });
  }

  async getTestimonialStats() {
    return this.request('/testimonials/stats/summary');
  }

  // Payment System APIs
  async getPaymentPackages() {
    return this.request('/payments/packages');
  }

  async createCheckoutSession(paymentData) {
    return this.request('/payments/checkout/session', {
      method: 'POST',
      body: paymentData,
    });
  }

  async getCheckoutStatus(sessionId) {
    return this.request(`/payments/checkout/status/${sessionId}`);
  }

  async getPaymentTransactions(params = {}) {
    const queryString = new URLSearchParams();
    
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryString.append(key, params[key]);
      }
    });

    return this.request(`/payments/transactions${queryString.toString() ? '?' + queryString.toString() : ''}`);
  }

  async getCorporateTiers() {
    return this.request('/corporate/tiers');
  }

  // Community APIs removed to maintain luxury brand focus

  // Marketing Automation APIs
  async setupBehavioralTrigger(triggerType, customerEmail, triggerData) {
    return this.request(`/marketing-automation/trigger/${triggerType}?customer_email=${encodeURIComponent(customerEmail)}`, {
      method: 'POST',
      body: triggerData,
    });
  }

  async createSeasonalCampaign(targetSegment = 'all') {
    return this.request(`/marketing-automation/campaigns/seasonal?target_segment=${targetSegment}`, {
      method: 'POST',
    });
  }

  async processScheduledCampaigns() {
    return this.request('/marketing-automation/campaigns/process', {
      method: 'POST',
    });
  }

  async getCampaignAnalytics(params = {}) {
    const queryString = new URLSearchParams();
    
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryString.append(key, params[key]);
      }
    });

    return this.request(`/marketing-automation/campaigns/analytics${queryString.toString() ? '?' + queryString.toString() : ''}`);
  }

  async getCaribbeanCulturalCalendar(month = null) {
    const query = month ? `?month=${encodeURIComponent(month)}` : '';
    return this.request(`/marketing-automation/cultural-calendar${query}`);
  }

  async getEmailTemplates(templateType = null) {
    const query = templateType ? `?template_type=${encodeURIComponent(templateType)}` : '';
    return this.request(`/marketing-automation/templates${query}`);
  }

  async getBehavioralTriggersConfig() {
    return this.request('/marketing-automation/triggers/config');
  }

  async createTestCampaign(testEmail, campaignType = 'abandoned_booking') {
    return this.request(`/marketing-automation/test-campaign?test_email=${encodeURIComponent(testEmail)}&campaign_type=${campaignType}`, {
      method: 'POST',
    });
  }

  async getMarketingPerformanceSummary(days = 30) {
    return this.request(`/marketing-automation/performance/summary?days=${days}`);
  }

  async getPerformanceDashboardData(days = 30) {
    return this.request(`/marketing-automation/performance/dashboard?days=${days}`);
  }

  // Enhanced Marketing Analytics APIs
  async exportAnalyticsCSV(days = 30, metricType = 'overview') {
    const backendUrl = getBackendURL();
    window.open(`${backendUrl}/api/marketing-analytics/export/csv?days=${days}&metric_type=${metricType}`, '_blank');
  }

  async getCampaignsDetailed(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    return this.request(`/marketing-analytics/campaigns/detailed?${queryParams}`);
  }

  async getCampaignDetails(campaignId) {
    return this.request(`/marketing-analytics/campaigns/${campaignId}/details`);
  }

  async getPeriodComparison(currentDays = 30, compareDays = 30) {
    return this.request(`/marketing-analytics/comparison?current_days=${currentDays}&compare_days=${compareDays}`);
  }

  async getAvailableMetrics() {
    return this.request(`/marketing-analytics/metrics/available`);
  }

  // Workflow Automation APIs
  async getWorkflows(status = null) {
    const params = status ? `?status=${status}` : '';
    return this.request(`/workflow-automation/workflows${params}`);
  }

  async getWorkflow(workflowId) {
    return this.request(`/workflow-automation/workflows/${workflowId}`);
  }

  async createWorkflow(workflowData) {
    return this.request(`/workflow-automation/workflows`, {
      method: 'POST',
      body: JSON.stringify(workflowData)
    });
  }

  async updateWorkflow(workflowId, updates) {
    return this.request(`/workflow-automation/workflows/${workflowId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  async deleteWorkflow(workflowId) {
    return this.request(`/workflow-automation/workflows/${workflowId}`, {
      method: 'DELETE'
    });
  }

  async executeWorkflow(workflowId, context) {
    return this.request(`/workflow-automation/workflows/${workflowId}/execute`, {
      method: 'POST',
      body: JSON.stringify({ context })
    });
  }

  async getWorkflowExecutions(workflowId, limit = 50) {
    return this.request(`/workflow-automation/workflows/${workflowId}/executions?limit=${limit}`);
  }

  async getWorkflowTemplates() {
    return this.request(`/workflow-automation/templates`);
  }

  async createWorkflowFromTemplate(templateId, name = null) {
    const params = name ? `?name=${encodeURIComponent(name)}` : '';
    return this.request(`/workflow-automation/templates/${templateId}/create${params}`, {
      method: 'POST'
    });
  }

  async getWorkflowAnalytics() {
    return this.request(`/workflow-automation/analytics`);
  }

  // Payment Management APIs
  async getPaymentAnalytics(days = 30) {
    return this.request(`/payment-management/analytics?days=${days}`);
  }

  async getPaymentTransactions(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    return this.request(`/payment-management/transactions?${queryParams}`);
  }

  async getPaymentTransactionDetails(transactionId) {
    return this.request(`/payment-management/transactions/${transactionId}`);
  }

  async processRefund(transactionId, refundData) {
    return this.request(`/payment-management/transactions/${transactionId}/refund`, {
      method: 'POST',
      body: JSON.stringify(refundData)
    });
  }

  async getInvoiceData(transactionId) {
    return this.request(`/payment-management/transactions/${transactionId}/invoice`);
  }

  async getRevenueTrends(days = 30) {
    return this.request(`/payment-management/revenue-trends?days=${days}`);
  }

  async recordManualPayment(paymentData) {
    return this.request(`/payment-management/manual-payment`, {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  async getPaymentDashboardSummary() {
    return this.request(`/payment-management/dashboard-summary`);
  }

  // Pi Network & XRP Bridge APIs
  async getPiXrpRates() {
    return this.request(`/pi-xrp-bridge/rates`);
  }

  async getPiXrpTierInfo(userEmail) {
    return this.request(`/pi-xrp-bridge/tier-info/${userEmail}`);
  }

  async calculatePiXrpReward(userEmail, baseAmount, cryptoCurrency) {
    return this.request(`/pi-xrp-bridge/calculate-reward?user_email=${userEmail}&base_amount=${baseAmount}&crypto_currency=${cryptoCurrency}`);
  }

  async createPiXrpReward(rewardData) {
    return this.request(`/pi-xrp-bridge/create-reward`, {
      method: 'POST',
      body: JSON.stringify(rewardData)
    });
  }

  async createBridgeTransfer(bridgeData) {
    return this.request(`/pi-xrp-bridge/bridge-transfer`, {
      method: 'POST',
      body: JSON.stringify(bridgeData)
    });
  }

  async getPiXrpWallet(userEmail) {
    return this.request(`/pi-xrp-bridge/wallet/${userEmail}`);
  }

  async updatePiXrpWalletAddress(userEmail, addressData) {
    return this.request(`/pi-xrp-bridge/wallet/${userEmail}/address`, {
      method: 'PUT',
      body: JSON.stringify(addressData)
    });
  }

  async getPiXrpAnalytics(days = 30) {
    return this.request(`/pi-xrp-bridge/analytics?days=${days}`);
  }

  async getPiXrpTierRewardsInfo() {
    return this.request(`/pi-xrp-bridge/tier-rewards-info`);
  }

  // AI Content Generation API methods
  async generateEmailSubjectLines(campaignType, targetAudience = 'luxury hospitality clients', count = 5) {
    const params = new URLSearchParams({
      campaign_type: campaignType,
      target_audience: targetAudience,
      count: count.toString()
    });
    return this.request(`/ai-content/generate-subject-lines?${params}`, {
      method: 'POST'
    });
  }

  async generateEmailContent(templateName, campaignType, targetSegment = 'luxury_hospitality_clients', culturalTheme = 'Caribbean heritage') {
    const params = new URLSearchParams({
      template_name: templateName,
      campaign_type: campaignType,
      target_segment: targetSegment,
      cultural_theme: culturalTheme
    });
    return this.request(`/ai-content/generate-email-content?${params}`, {
      method: 'POST'
    });
  }

  async generateCampaignDescription(campaignName, campaignType, targetAudience = 'luxury hospitality clients') {
    const params = new URLSearchParams({
      campaign_name: campaignName,
      campaign_type: campaignType,
      target_audience: targetAudience
    });
    return this.request(`/ai-content/generate-campaign-description?${params}`, {
      method: 'POST'
    });
  }

  async generateCulturalStory(storyTheme = 'Caribbean heritage', serviceConnection = 'luxury hospitality') {
    const params = new URLSearchParams({
      story_theme: storyTheme,
      service_connection: serviceConnection
    });
    return this.request(`/ai-content/generate-cultural-story?${params}`, {
      method: 'POST'
    });
  }

  async optimizeContentForSegment(content, targetSegment, optimizationGoal = 'engagement') {
    const params = new URLSearchParams({
      content: content,
      target_segment: targetSegment,
      optimization_goal: optimizationGoal
    });
    return this.request(`/ai-content/optimize-content?${params}`, {
      method: 'POST'
    });
  }

  async getAIContentSuggestions() {
    return this.request('/ai-content/content-suggestions');
  }

  // Customer Intelligence APIs
  async getCustomerProfileIntelligence(customerEmail) {
    return this.request(`/customer-intelligence/profile/${encodeURIComponent(customerEmail)}`);
  }

  async getCustomerSegmentation() {
    return this.request('/customer-intelligence/segmentation');
  }

  async getCustomerJourneyAnalytics(days = 30) {
    return this.request(`/customer-intelligence/journey-analytics?days=${days}`);
  }

  async getTopCustomers(params = {}) {
    const queryString = new URLSearchParams();
    
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryString.append(key, params[key]);
      }
    });

    return this.request(`/customer-intelligence/top-customers${queryString.toString() ? '?' + queryString.toString() : ''}`);
  }

  async getEngagementTrends(days = 30) {
    return this.request(`/customer-intelligence/engagement-trends?days=${days}`);
  }

  async trackCustomerInteraction(interactionData) {
    return this.request('/customer-intelligence/track-interaction', {
      method: 'POST',
      body: interactionData,
    });
  }

  // Business Automation APIs (for completeness)
  async getAutomationWorkflows() {
    return this.request('/business-automation/workflows');
  }

  async getBusinessMetrics(days = 30) {
    return this.request(`/business-automation/metrics?days=${days}`);
  }

  async getDynamicPricing(serviceType) {
    return this.request(`/business-automation/dynamic-pricing/${serviceType}`);
  }

  // Crypto Payment APIs
  async getCryptoRates() {
    return this.request('/crypto-payments/rates');
  }

  async calculateCryptoAmount(usdAmount, cryptoCurrency) {
    return this.request('/crypto-payments/calculate', {
      method: 'POST',
      body: { usd_amount: usdAmount, crypto_currency: cryptoCurrency }
    });
  }

  async createCryptoPayment(paymentData) {
    return this.request('/crypto-payments/create', {
      method: 'POST',
      body: paymentData
    });
  }

  async getCryptoPayments(params = {}) {
    const queryString = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryString.append(key, params[key]);
      }
    });
    return this.request(`/crypto-payments/payments${queryString.toString() ? '?' + queryString.toString() : ''}`);
  }

  async getCryptoPayment(paymentId) {
    return this.request(`/crypto-payments/payments/${paymentId}`);
  }

  async updateCryptoPaymentStatus(paymentId, status) {
    return this.request(`/crypto-payments/payments/${paymentId}/status`, {
      method: 'PUT',
      body: { status }
    });
  }

  async getCryptoAnalytics(days = 30) {
    return this.request(`/crypto-payments/analytics?days=${days}`);
  }

  // Workflow Automation APIs
  async getWorkflows() {
    return this.request('/workflow-automation/workflows');
  }

  async createWorkflow(workflowData) {
    return this.request('/workflow-automation/workflows', {
      method: 'POST',
      body: workflowData
    });
  }

  async updateWorkflow(workflowId, workflowData) {
    return this.request(`/workflow-automation/workflows/${workflowId}`, {
      method: 'PUT',
      body: workflowData
    });
  }

  async deleteWorkflow(workflowId) {
    return this.request(`/workflow-automation/workflows/${workflowId}`, {
      method: 'DELETE'
    });
  }

  async executeWorkflow(workflowId, inputData = {}) {
    return this.request(`/workflow-automation/workflows/${workflowId}/execute`, {
      method: 'POST',
      body: inputData
    });
  }

  async getWorkflowTemplates() {
    return this.request('/workflow-automation/templates');
  }

  async createWorkflowFromTemplate(templateId) {
    return this.request(`/workflow-automation/templates/${templateId}/create-workflow`, {
      method: 'POST'
    });
  }

  async getWorkflowAnalytics() {
    return this.request('/workflow-automation/analytics');
  }

  // Real-Time Metrics API
  async getRealtimeDashboardMetrics() {
    return this.request('/realtime/dashboard-metrics');
  }

  async getRecentActivity(limit = 20) {
    return this.request(`/realtime/recent-activity?limit=${limit}`);
  }

  async getActiveSessions() {
    return this.request('/realtime/active-sessions');
  }

  async getSystemStatus() {
    return this.request('/realtime/system-status');
  }

  // Alerts API
  async getActiveAlerts(acknowledged = false) {
    return this.request(`/alerts/active?acknowledged=${acknowledged}`);
  }

  async getAlertSummary() {
    return this.request('/alerts/summary');
  }

  async acknowledgeAlert(alertId) {
    return this.request(`/alerts/acknowledge/${alertId}`, {
      method: 'POST'
    });
  }

  async checkSystemAlerts() {
    return this.request('/alerts/check-system', {
      method: 'POST'
    });
  }

  // Metrics History API
  async getMetricsHistory(hours = 24) {
    return this.request(`/metrics-history/history?hours=${hours}`);
  }

  async getMetricsSummary(hours = 24) {
    return this.request(`/metrics-history/summary?hours=${hours}`);
  }

  async getTrendAnalysis(hours = 24) {
    return this.request(`/metrics-history/trends?hours=${hours}`);
  }

  async getChartData(hours = 24, metric = 'all') {
    return this.request(`/metrics-history/chart-data?hours=${hours}&metric=${metric}`);
  }

  async recordMetrics() {
    return this.request('/metrics-history/record', {
      method: 'POST'
    });
  }

  // Export API
  async exportMetricsHistory(hours = 24, format = 'csv') {
    const backendUrl = getBackendURL();
    window.open(`${backendUrl}/api/exports/metrics-history?hours=${hours}&format=${format}`, '_blank');
  }

  async exportDashboardSnapshot(format = 'json') {
    const backendUrl = getBackendURL();
    window.open(`${backendUrl}/api/exports/dashboard-snapshot?format=${format}`, '_blank');
  }

  async exportAlerts(format = 'csv', acknowledged = false) {
    const backendUrl = getBackendURL();
    window.open(`${backendUrl}/api/exports/alerts?format=${format}&acknowledged=${acknowledged}`, '_blank');
  }

  async exportPerformanceReport(format = 'json') {
    const backendUrl = getBackendURL();
    window.open(`${backendUrl}/api/exports/performance-report?format=${format}`, '_blank');
  }

  // Virtual Chef Consultations APIs
  async getAvailableChefs() {
    return this.request('/consultations/chefs/available');
  }

  async createConsultation(consultationData) {
    return this.request('/consultations/', {
      method: 'POST',
      body: consultationData
    });
  }

  async getConsultations() {
    return this.request('/consultations/');
  }

  async getConsultation(consultationId) {
    return this.request(`/consultations/${consultationId}`);
  }

  // Meal Subscriptions APIs
  async getMealPlans() {
    return this.request('/meal-subscriptions/plans');
  }

  async createMealSubscription(subscriptionData) {
    return this.request('/meal-subscriptions/', {
      method: 'POST',
      body: subscriptionData
    });
  }

  async getMealSubscriptions() {
    return this.request('/meal-subscriptions/');
  }

  async getWeeklyMenu() {
    return this.request('/meal-subscriptions/menu/this-week');
  }

  // Chef Marketplace APIs
  async getMarketplaceChefs() {
    return this.request('/chef-marketplace/chefs');
  }

  async getChefProfile(chefId) {
    return this.request(`/chef-marketplace/chefs/${chefId}`);
  }

  async createChefBooking(bookingData) {
    return this.request('/chef-marketplace/bookings', {
      method: 'POST',
      body: bookingData
    });
  }

  // Event Planning APIs
  async getEventTemplates() {
    return this.request('/event-planning/templates');
  }

  async createEventRequest(eventData) {
    return this.request('/event-planning/', {
      method: 'POST',
      body: eventData
    });
  }

  async getEventRequests() {
    return this.request('/event-planning/');
  }
}

// Helper functions that match the mock data structure
const apiService = new ApiService();

// Export api as default and named export for compatibility
export const api = apiService;
export { apiService };

// Form handlers that replace mock handlers
export const formHandlers = {
  subscribeToNewsletter: async (email) => {
    try {
      const result = await apiService.subscribeToNewsletter(email);
      return {
        success: result.success || true,
        message: result.message || 'Successfully subscribed to newsletter!',
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to subscribe to newsletter',
      };
    }
  },

  signupForEvents: async (email) => {
    try {
      const result = await apiService.signupForEvents(email);
      return {
        success: result.success || true,
        message: result.message || 'Successfully signed up for pop-up events!',
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to sign up for events',
      };
    }
  },

  submitEventInquiry: async (inquiryData) => {
    try {
      const result = await apiService.submitEventInquiry(inquiryData);
      return {
        success: result.success || true,
        message: result.message || 'Event inquiry submitted successfully!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to submit event inquiry',
      };
    }
  },

  submitCakeInquiry: async (inquiryData) => {
    try {
      const result = await apiService.submitCakeInquiry(inquiryData);
      return {
        success: result.success || true,
        message: result.message || 'Cake inquiry submitted successfully!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to submit cake inquiry',
      };
    }
  },

  subscribeToTier: async (subscriptionData) => {
    try {
      const result = await apiService.subscribeToTier(subscriptionData);
      return {
        success: result.success || true,
        message: result.message || 'Successfully subscribed to tier!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to subscribe to tier',
      };
    }
  },

  submitPartnershipApplication: async (applicationData) => {
    try {
      const result = await apiService.submitPartnershipApplication(applicationData);
      return {
        success: result.success || true,
        message: result.message || 'Partnership application submitted successfully!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to submit partnership application',
      };
    }
  },

  purchaseGiftCard: async (giftCardData) => {
    try {
      const result = await apiService.purchaseGiftCard(giftCardData);
      return {
        success: result.success || true,
        message: result.message || 'Gift card purchased successfully!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to purchase gift card',
      };
    }
  },

  createReferral: async (referralData) => {
    try {
      const result = await apiService.createReferral(referralData);
      return {
        success: result.success || true,
        message: result.message || 'Referral created successfully!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to create referral',
      };
    }
  },

  submitFeedback: async (feedbackData) => {
    try {
      const result = await apiService.submitFeedback(feedbackData);
      return {
        success: result.success || true,
        message: result.message || 'Feedback submitted successfully!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to submit feedback',
      };
    }
  },

  submitVolunteerApplication: async (applicationData) => {
    try {
      const result = await apiService.submitVolunteerApplication(applicationData);
      return {
        success: result.success || true,
        message: result.message || 'Volunteer application submitted successfully!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to submit volunteer application',
      };
    }
  },

  submitFarmToTableBooking: async (bookingData) => {
    try {
      const result = await apiService.submitFarmToTableBooking(bookingData);
      return {
        success: result.success || true,
        message: result.message || 'Farm-to-table booking submitted successfully!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to submit farm-to-table booking',
      };
    }
  },

  submitGiftPackageOrder: async (orderData) => {
    try {
      const result = await apiService.submitGiftPackageOrder(orderData);
      return {
        success: result.success || true,
        message: result.message || 'Gift package order submitted successfully!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to submit gift package order',
      };
    }
  },

  submitCorporateInquiry: async (inquiryData) => {
    try {
      const result = await apiService.submitCorporateInquiry(inquiryData);
      return {
        success: result.success || true,
        message: result.message || 'Corporate inquiry submitted successfully!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to submit corporate inquiry',
      };
    }
  },

  submitTestimonial: async (testimonialData) => {
    try {
      const result = await apiService.submitTestimonial(testimonialData);
      return {
        success: result.success || true,
        message: result.message || 'Testimonial submitted successfully!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to submit testimonial',
      };
    }
  },

  // Community form handlers removed to maintain luxury brand focus

  // Marketing Automation Form Handlers
  setupMarketingTrigger: async (triggerType, customerEmail, triggerData) => {
    try {
      const result = await apiService.setupBehavioralTrigger(triggerType, customerEmail, triggerData);
      return {
        success: result.success || true,
        message: result.message || 'Marketing trigger setup successfully!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to setup marketing trigger',
      };
    }
  },

  createMarketingCampaign: async (targetSegment) => {
    try {
      const result = await apiService.createSeasonalCampaign(targetSegment);
      return {
        success: result.success || true,
        message: result.message || 'Campaign created successfully!',
        data: result.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to create campaign',
      };
    }
  }
};

// Page tracking utility
export const trackPageView = async (page) => {
  try {
    const userAgent = navigator.userAgent;
    await apiService.trackPageView(page, userAgent);
  } catch (error) {
    console.warn('Failed to track page view:', error);
  }
};

// Initialize page tracking on app load
export const initializeAnalytics = () => {
  // Track initial page load
  trackPageView(window.location.pathname);

  // Track page changes for SPA
  let lastUrl = window.location.href;
  new MutationObserver(() => {
    const currentUrl = window.location.href;
    if (currentUrl !== lastUrl) {
      lastUrl = currentUrl;
      trackPageView(window.location.pathname);
    }
  }).observe(document, { subtree: true, childList: true });
};

// ============================================================================
// NEW FEATURES API METHODS
// ============================================================================

// Virtual Chef Consultations API
apiService.getAvailableChefs = async function() {
  return this.request('/consultations/chefs/available', { method: 'GET' });
};

apiService.createConsultation = async function(consultationData) {
  return this.request('/consultations/', {
    method: 'POST',
    body: consultationData
  });
};

apiService.getConsultations = async function(filters = {}) {
  const params = new URLSearchParams(filters).toString();
  return this.request(`/consultations${params ? '?' + params : ''}`, { method: 'GET' });
};

// Meal Subscriptions API
apiService.getMealPlans = async function() {
  return this.request('/meal-subscriptions/plans', { method: 'GET' });
};

apiService.createMealSubscription = async function(subscriptionData) {
  return this.request('/meal-subscriptions/', {
    method: 'POST',
    body: subscriptionData
  });
};

apiService.getWeeklyMenu = async function() {
  return this.request('/meal-subscriptions/menu/this-week', { method: 'GET' });
};

// Private Chef Marketplace API
apiService.getMarketplaceChefs = async function(filters = {}) {
  const params = new URLSearchParams(filters).toString();
  return this.request(`/chef-marketplace/chefs${params ? '?' + params : ''}`, { method: 'GET' });
};

apiService.getChefProfile = async function(chefId) {
  return this.request(`/chef-marketplace/chefs/${chefId}`, { method: 'GET' });
};

apiService.createChefBooking = async function(bookingData) {
  return this.request('/chef-marketplace/bookings', {
    method: 'POST',
    body: bookingData
  });
};

// Event Planning API
apiService.getEventTemplates = async function() {
  return this.request('/event-planning/templates', { method: 'GET' });
};

apiService.createEventRequest = async function(eventData) {
  return this.request('/event-planning/', {
    method: 'POST',
    body: eventData
  });
};

apiService.getEventRequests = async function(filters = {}) {
  const params = new URLSearchParams(filters).toString();
  return this.request(`/event-planning${params ? '?' + params : ''}`, { method: 'GET' });
};

export default apiService;